import Highcharts from '~/scripts/integrations/highcharts';

// Industry presets: [averageSalary, turnoverRatePercent, replacementCostPercentOfSalary]
const INDUSTRY_PRESETS = {
  technology:    { salary: 95000, turnover: 13, replacement: 100 },
  healthcare:    { salary: 65000, turnover: 20, replacement: 90 },
  finance:       { salary: 75000, turnover: 13, replacement: 110 },
  manufacturing: { salary: 55000, turnover: 20, replacement: 60 },
  retail:        { salary: 35000, turnover: 60, replacement: 40 },
  hospitality:   { salary: 38000, turnover: 50, replacement: 40 },
  call_center:   { salary: 38000, turnover: 35, replacement: 50 },
  education:     { salary: 45000, turnover: 16, replacement: 80 },
  construction:  { salary: 50000, turnover: 21, replacement: 70 }
};

// Approximate share of replacement cost by driver (must sum to 1)
const COST_BREAKDOWN = [
  { name: 'Lost productivity & vacancy', share: 0.35, color: '#dc3545' },
  { name: 'Recruiting & hiring',         share: 0.25, color: '#fd7e14' },
  { name: 'Onboarding & training',       share: 0.25, color: '#ffc107' },
  { name: 'Ramp-up to full output',      share: 0.15, color: '#6f42c1' }
];

class EmployeeTurnoverCostCalculator {
  constructor() {
    this.form = document.getElementById('calculator-form');
    this.industryInput = document.getElementById('industry-preset');
    this.employeeCountInput = document.getElementById('employee-count');
    this.salaryInput = document.getElementById('average-salary');
    this.turnoverRateInput = document.getElementById('turnover-rate');
    this.replacementInput = document.getElementById('replacement-cost-percent');
    this.reductionInput = document.getElementById('recognition-reduction');

    this.totalCostEl = document.getElementById('total-cost');
    this.departuresEl = document.getElementById('annual-departures');
    this.costPerDepartureEl = document.getElementById('cost-per-departure');
    this.savingsEl = document.getElementById('potential-savings');
    this.costTextEl = document.getElementById('cost-text');
    this.savingsTextEl = document.getElementById('savings-text');
    this.resetBtn = document.getElementById('reset-turnover-calculator');

    this.savingsChart = null;
    this.breakdownChart = null;

    this.bindEvents();
    this.calculate();
  }

  bindEvents() {
    [
      this.employeeCountInput,
      this.salaryInput,
      this.turnoverRateInput,
      this.replacementInput,
      this.reductionInput
    ].forEach((input) => {
      input.addEventListener('input', () => this.calculate());
      input.addEventListener('change', () => this.calculate());
    });

    this.industryInput.addEventListener('change', () => this.applyPreset());

    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', () => this.reset());
    }
  }

  applyPreset() {
    const preset = INDUSTRY_PRESETS[this.industryInput.value];
    if (preset) {
      this.salaryInput.value = preset.salary;
      this.turnoverRateInput.value = preset.turnover;
      this.replacementInput.value = preset.replacement;
    }
    this.calculate();
  }

  reset() {
    this.form.reset();
    this.applyPreset();
  }

  formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.round(value));
  }

  readNumber(input, fallback = 0) {
    const value = parseFloat(input.value);
    return Number.isFinite(value) && value >= 0 ? value : fallback;
  }

  calculate() {
    const employeeCount = this.readNumber(this.employeeCountInput);
    const salary = this.readNumber(this.salaryInput);
    const turnoverRate = this.readNumber(this.turnoverRateInput) / 100;
    const replacementRate = this.readNumber(this.replacementInput) / 100;
    const reductionRate = this.readNumber(this.reductionInput) / 100;

    const departures = employeeCount * turnoverRate;
    const costPerDeparture = salary * replacementRate;
    const totalCost = departures * costPerDeparture;
    const savings = totalCost * reductionRate;

    this.totalCostEl.textContent = this.formatCurrency(totalCost);
    this.departuresEl.textContent = departures.toLocaleString('en-US', {
      maximumFractionDigits: 1
    });
    this.costPerDepartureEl.textContent = this.formatCurrency(costPerDeparture);
    this.savingsEl.textContent = this.formatCurrency(savings);

    this.costTextEl.innerHTML = `
      Losing about <strong>${departures.toLocaleString('en-US', { maximumFractionDigits: 1 })}</strong>
      of your ${employeeCount.toLocaleString()} employees each year at
      ${this.formatCurrency(costPerDeparture)} per departure adds up to
      <strong>${this.formatCurrency(totalCost)}</strong> annually.
    `;

    this.savingsTextEl.innerHTML = `
      A ${(reductionRate * 100).toFixed(0)}% reduction in turnover would keep roughly
      <strong>${(departures * reductionRate).toLocaleString('en-US', { maximumFractionDigits: 1 })}</strong>
      more employees and save an estimated
      <strong>${this.formatCurrency(savings)}</strong> per year.
    `;

    this.updateSavingsChart(totalCost, totalCost - savings);
    this.updateBreakdownChart(costPerDeparture);
  }

  updateSavingsChart(currentCost, reducedCost) {
    if (this.savingsChart) {
      this.savingsChart.destroy();
    }

    this.savingsChart = Highcharts.chart('turnover-savings-chart', {
      chart: { type: 'column', height: 320 },
      title: { text: null },
      credits: { enabled: false },
      xAxis: { categories: ['Current turnover cost', 'With recognition'] },
      yAxis: {
        min: 0,
        title: { text: 'Annual cost (USD)' },
        labels: { format: '${value:,.0f}' }
      },
      legend: { enabled: false },
      tooltip: { pointFormat: '<b>${point.y:,.0f}</b>' },
      series: [{
        name: 'Annual turnover cost',
        colorByPoint: true,
        colors: ['#dc3545', '#28a745'],
        data: [Math.round(currentCost), Math.round(reducedCost)]
      }]
    });
  }

  updateBreakdownChart(costPerDeparture) {
    const data = COST_BREAKDOWN.map((item) => ({
      name: item.name,
      y: Math.round(costPerDeparture * item.share),
      color: item.color
    }));

    if (this.breakdownChart) {
      this.breakdownChart.destroy();
    }

    this.breakdownChart = Highcharts.chart('turnover-breakdown-chart', {
      chart: { type: 'pie', height: 320 },
      title: { text: null },
      credits: { enabled: false },
      tooltip: { pointFormat: '<b>${point.y:,.0f}</b> ({point.percentage:.0f}%)' },
      plotOptions: {
        pie: {
          dataLabels: {
            enabled: true,
            format: '{point.name}: {point.percentage:.0f}%',
            style: { fontSize: '11px' }
          }
        }
      },
      series: [{ name: 'Cost per departure', data }]
    });
  }
}

export default EmployeeTurnoverCostCalculator;
